import {useCallback, useEffect, useRef, useState} from 'react';

/** Represents a geographic position with latitude and longitude. */
interface GeoPosition {
    /** Accuracy of the position in meters. */
    accuracy: number;
    /** Latitude in decimal degrees. */
    latitude: number;
    /** Longitude in decimal degrees. */
    longitude: number;
}

/** Return type of the `useGeolocation` hook. */
interface UseGeolocationResult {
    /** The last known error message, or null. */
    error: string | null;
    /** Whether the browser is actively tracking the position. */
    isTracking: boolean;
    /** The current user position, or null if unavailable. */
    position: GeoPosition | null;
    /** Starts continuous position tracking. */
    startTracking(): void;
    /** Stops position tracking. */
    stopTracking(): void;
}

/** Options for the continuous watch — always fresh, GPS-level accuracy. */
const WATCH_OPTIONS: PositionOptions = {
    enableHighAccuracy: true,
    maximumAge: 0,
    timeout: 30_000
};

/** Lenient options for the initial fast fix (allows cached position). */
const INITIAL_OPTIONS: PositionOptions = {
    enableHighAccuracy: false,
    maximumAge: 60_000,
    timeout: 10_000
};

/**
 * Extracts a `GeoPosition` from a native `GeolocationPosition`.
 *
 * @param pos The raw browser position.
 * @returns A plain `GeoPosition` object.
 */
const toGeoPosition = (pos: GeolocationPosition): GeoPosition => ({
    accuracy: pos.coords.accuracy,
    latitude: pos.coords.latitude,
    longitude: pos.coords.longitude
});

/**
 * Custom hook that provides continuous geolocation tracking with high accuracy.
 * On mount it first acquires a quick coarse fix via `getCurrentPosition`, then
 * starts a `watchPosition` with `maximumAge: 0` to get the most precise GPS data.
 * If the watch fails it retries once after a short delay.
 *
 * @param enabled Whether geolocation tracking is permitted by the user setting. Defaults to `true`.
 * @returns An object containing the current position, tracking state, and control functions.
 */
export const useGeolocation = (enabled = true): UseGeolocationResult => {
    const [position, setPosition] = useState<GeoPosition | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isTracking, setIsTracking] = useState(false);
    const watchIdRef = useRef<number | null>(null);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mountedRef = useRef(true);

    /**
     * Stops the active geolocation watch and any pending retry timer.
     */
    const stopTracking = useCallback(() => {
        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }

        if (retryTimerRef.current !== null) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }

        setIsTracking(false);
    }, []);

    /**
     * Starts the continuous `watchPosition` listener.
     *
     * @param isRetry Whether this is a retry attempt (suppresses duplicate retry).
     */
    const startWatch = useCallback((isRetry = false) => {
        if (watchIdRef.current !== null) return;
        if (!mountedRef.current) return;

        watchIdRef.current = navigator.geolocation.watchPosition(
            pos => {
                if (!mountedRef.current) return;
                setPosition(toGeoPosition(pos));
                setError(null);
            },
            err => {
                if (!mountedRef.current) return;
                setError(err.message);

                /* Retry once on timeout — some browsers need a kick after reload. */
                if (!isRetry && err.code === err.TIMEOUT) {
                    if (watchIdRef.current !== null) {
                        navigator.geolocation.clearWatch(watchIdRef.current);
                        watchIdRef.current = null;
                    }

                    retryTimerRef.current = setTimeout(() => {
                        retryTimerRef.current = null;
                        startWatch(true);
                    }, 2_000);
                }
            },
            WATCH_OPTIONS
        );
    }, []);

    /**
     * Starts continuous position tracking via the Geolocation API.
     * First acquires a fast coarse fix, then switches to high-accuracy watching.
     */
    const startTracking = useCallback(() => {
        if (!('geolocation' in navigator)) {
            setError('Geolocation wird von diesem Browser nicht unterstützt.');

            return;
        }

        if (watchIdRef.current !== null) return;

        setError(null);
        setIsTracking(true);

        /* Get a quick initial position (may be coarse / cached). */
        navigator.geolocation.getCurrentPosition(
            pos => {
                if (!mountedRef.current) return;
                setPosition(toGeoPosition(pos));
            },
            () => {
                /* Ignore — the watch will provide positions soon. */
            },
            INITIAL_OPTIONS
        );

        /* Start the high-accuracy continuous watch immediately in parallel. */
        startWatch();
    }, [startWatch]);

    useEffect(() => {
        mountedRef.current = true;

        if (enabled) {
            startTracking();
        } else {
            stopTracking();
            setPosition(null);
            setError(null);
        }

        return () => {
            mountedRef.current = false;
            stopTracking();
        };
    }, [enabled, startTracking, stopTracking]);

    return {
        error,
        isTracking,
        position,
        startTracking,
        stopTracking
    };
};